<template>
	<button
		:class="['tab-item', tabItemClasses]"
		:aria-label="title"
		role="tab"
		type="button"
		:aria-selected="active"
		:tabindex="disabled ? -1 : 0"
		@click.stop="onClick"
	>
		<slot v-bind="{ ...props }">
			<p
				class="tab-item__title"
				role="presentation"
			>
				<slot
					v-bind="{ ...props }"
					name="title"
				>
					{{ title }}
				</slot> <slot
					v-bind="{ ...props }"
					name="suffix-title"
				/>
			</p>
			<slot
				name="subtext"
				v-bind="{ ...props }"
			>
				<p
					v-if="subText"
					class="tab-item__subtext"
				>
					{{ subText }} <slot
						v-bind="{ ...props }"
						name="suffix-subtext"
					/>
				</p>
			</slot>
		</slot>
	</button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

export interface TabItemProps {
	title: string,
	subText?: string,
	disabled?: boolean,
	active?: boolean,
}
const emit = defineEmits(['selected']);
const props = withDefaults(defineProps<TabItemProps>(), {
	active: false,
	disabled: false,
	subText: undefined
});

const tabItemClasses = computed(() => ({
	'tab-item--active': props.active,
	'tab-item--disabled': props.disabled,
}));

const onClick = () => {
	if (!props.disabled) {
		emit('selected');
	}
};
</script>

<style lang="scss" scoped>
.tab-item {
	& * {
		padding: 0;
		margin: 0;
	}

	& {
		transition: all 200ms ease;
		font-family: $font-family;
		position: relative;
		background: none;
		display: inline-flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		border: none;
		flex: 1;
		padding: 2.4rem 0.8rem;
		color: $color-text-label;
	}

	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 0.4rem;
		background: $color-text-upselling;
		opacity: 0;
		transition: opacity 0.3s;
	}

	&__title {
		font-weight: $font-weight-semibold;
		font-size: $font-small-1;
		line-height: 2.3rem;
	}

	&__subtext {
		font-weight: $font-weight-regular;
		line-height: 2.3rem;
		font-size: $font-small-2;
	}

	&--disabled {
		color: $color-inactive;
		cursor: not-allowed;
	}

	&--active {
		color: $color-text-upselling;

		.tab-item__title {
			font-weight: $font-weight-bold;
		}

		&::after {
			opacity: 1;
		}

		.tab-item--disabled {
			color: $color-placeholder-text;
			background-color: #f9f9f9;

			&::after {
				background: $color-placeholder-text;
			}
		}
	}

	&:hover:not(&--disabled) {
		background-color: $color-primary-l6;
		color: $color-text-upselling;

		&.tab-item--active {
			color: $color-hover-active-color-tablist;

			&::after {
				background: $color-hover-active-color-tablist;
			}
		}
	}
}
</style>
